import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import FixtureGate from "../Changes/FixtureGate";
import { ZOOMS as ISSUES_ZOOMS } from "../Issues/Pivot";
import { DEFAULT_MEASURE } from "../Issues/measure";
import { usePushParams } from "../CurrentMonitoredUri/commons";
import { usePageFiles, useDeepDiveFixture } from "./fixture";
import { ADDED_COLORS, addedIdsOf, suggestionsOf } from "./compare";
import { daysAround, daysOf, defaultSinceOf, startsOf } from "./figures";
import { chartOf, openingSelectionOf } from "./selection";
import { metricsOf } from "./chartData";
import { changePointMarkersOf, issueMarkersOf, startOfMarker } from "./markers";
import { SERIES } from "./series";
import Header from "./Header";
import Chart from "./Chart";
import Summary from "./Summary";
import CompareDrawer from "./CompareDrawer";
import CompareCall from "./CompareCall";
import Zoom from "./Zoom";

// A month cannot show both 30-day periods around the issue start, so the chart would widen it to about two.
const ZOOMS = ISSUES_ZOOMS.filter(({ key }) => key !== "1m");
const DEFAULT_ZOOM = "12m";

/**
 * One page's signals around an issue's start, drawn as its Issues row draws them but with every signal
 * the page has, and other pages as more lines on the same chart.
 */
export default function IssueDeepDive() {
    const { workspaceNumber, id } = useParams();
    const location = useLocation();
    const pushParams = usePushParams();
    const fromIssues = useSelector((state) => state.issueDeepDive.fromIssues);
    const { site, page, error } = useDeepDiveFixture(id);
    const params = new URLSearchParams(location.search);
    const addedIds = site ? addedIdsOf(params, id, site.pages) : [];
    const addedPages = usePageFiles(addedIds);
    const [comparing, setComparing] = useState(false);
    // The periods are page state, not the URL: a drag would push a history entry per frame.
    const [kept, setKept] = useState(null);
    const [picked, setPicked] = useState(null);
    const [zoomed, setZoomed] = useState(null);
    const [hidden, setHidden] = useState(null);
    const closeCompare = useCallback(() => setComparing(false), []);
    // A page taken off the chart is no longer hidden when it is added again.
    const addedKey = addedIds.join();
    useEffect(() => {
        setHidden((current) => current && current.ids.some((each) => !addedIds.includes(each))
            ? { ...current, ids: current.ids.filter((each) => addedIds.includes(each)) }
            : current);
    }, [addedKey]);

    if (!page) {
        return <FixtureGate error={error} />;
    }

    const starts = startsOf(page.issues);
    const since = starts.includes(params.get("since")) ? params.get("since") : defaultSinceOf(page.issues);
    const zoom = ZOOMS.find(({ key }) => key === (zoomed?.id === id ? zoomed.key : DEFAULT_ZOOM));
    // Issues starting on the same day share their readings, as on the Issues rows.
    const issue = page.issues.find((each) => each.since === since && each.sinceKind !== "neverHealthy");
    const days = daysOf(site.chart);
    const spanDays = since ? daysAround(days, since, zoom.days) : days;
    const opening = openingSelectionOf(since, site.chart.end);
    const own = kept?.id === id && kept.since === since ? kept : { selection: opening, held: opening };
    const selection = own.selection;
    // Widened by the periods as they open or as they stood when the zoom changed, never by a drag's.
    const chart = chartOf({ start: spanDays[0], end: spanDays[spanDays.length - 1] }, own.held);
    const chartDays = daysOf(chart);

    // Until the reader picks a tab on this page, the chart draws the signal the Issues list was reading.
    const chosen = picked?.id === id ? picked.metric : null;
    const measure = fromIssues?.measure || DEFAULT_MEASURE;
    const series = SERIES.find(({ key }) => key === chosen) || SERIES.find(({ key }) => key === measure) || SERIES[0];

    // A click on one of this page's issue markers also centres the chart on that start.
    const changeSelection = ({ selection: next, markerId, metric, cause }) => {
        if (cause === "tab") {
            setPicked({ id, metric });
            return;
        }
        const start = markerId && starts.includes(startOfMarker(markerId)) ? startOfMarker(markerId) : null;
        setKept({ id, since: start ?? since, selection: next, held: start && start !== since ? openingSelectionOf(start, site.chart.end) : own.held });
        if (start && start !== since) pushParams({ since: start === starts[0] ? null : start });
    };
    const changeZoom = (key) => {
        setKept({ id, since, selection, held: selection });
        setZoomed({ id, key });
    };
    const added = addedIds.map((addedId, index) => {
        const file = addedPages.get(addedId);
        return {
            id: addedId,
            path: site.pages.find((each) => each.id === addedId).path,
            color: ADDED_COLORS[index],
            metrics: file ? metricsOf(file, site, chart) : file,
        };
    });
    const hiddenIds = hidden?.id === id ? hidden.ids : [];
    const toggleHidden = (pageId) => setHidden({ id, ids: hiddenIds.includes(pageId) ? hiddenIds.filter((each) => each !== pageId) : [...hiddenIds, pageId] });
    // The pages with a rising pivot at the start: the suggestions that carry the pivot's day.
    const offered = suggestionsOf(since ? page.compare[since] : undefined, page.id).filter(({ reasons }) => reasons.some(({ day }) => day)).length;
    const pageRoute = `/ws/${workspaceNumber}/monitored-uris/${page.id}`;
    // Pettitt's marker first, so an issue start's marker a day from it is drawn on top and stays clickable.
    const markers = [...changePointMarkersOf(issue, series.key, since), ...issueMarkersOf(page.issues)];

    return (
        <Wrap>
            <Header page={page} issuesRoute={`/ws/${workspaceNumber}/issues`} pageRoute={pageRoute}
                    controls={since && <Zoom zooms={ZOOMS} zoom={zoom} onZoom={changeZoom} />} />
            <Blocks>
                <CompareCall offered={offered} since={since} onOpen={() => setComparing(true)} />
                <Chart metrics={metricsOf(page, site, chart)} metric={series.key} chart={chart} selection={selection}
                       onChange={changeSelection} markers={markers}
                       added={added} hiddenIds={hiddenIds} onToggleHidden={toggleHidden} page={page} />
                <Summary page={page} site={site} issue={issue} since={since} pageRoute={pageRoute} />
                <CompareDrawer open={comparing} onClose={closeCompare} page={page} added={added} indexPages={site.pages} compare={since ? page.compare[since] : undefined} since={since}
                               days={days} shownDays={chartDays.filter((day) => day >= site.chart.start && day <= site.chart.end)} />
            </Blocks>
        </Wrap>
    );
}

const Wrap = styled.div`
    padding-bottom: 40px;
`;

const Blocks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;
